import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate,
} from '@angular/material/datepicker';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { Language } from '@core/interfaces/language';
import { Subscription } from 'rxjs';

import { TLanguage } from '@core/interfaces/TBot';
import { TBotItem } from '@core/interfaces/TBotItem';

import { TCountry, TRegion } from '@core/api/countries/types';
import { BotsService } from '@shared/services/bots.service';
import { CountriesService } from '@shared/services/countries.service';
import { ProjectsService } from '@shared/services/projects.service';
import { AnchoredPanelController } from '@shared/utils/anchored-panel.controller';

const DASHBOARD_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-conversation-filters',
  templateUrl: './conversation-filters.component.html',
  styleUrls: ['./conversation-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    '[class.visible]': 'panel.isVisible()',
    '[style.display]': 'panel.display()',
    '[style.position]': '"fixed"',
    '[style.z-index]': '"1000"',
    '[style.top]': 'panel.top()',
    '[style.bottom]': 'panel.bottom()',
    '[style.left]': 'panel.left()',
    '[style.width]': 'panel.width()',
    '[style.max-width]': '"800px"',
    '[style.--anchored-panel-max-height]': 'panel.maxHeight()',
  },
  providers: [
    // Scoped Moment adapter + formats: root adapter uses global YYYY-MM-DD and ignores
    // component-only MAT_DATE_FORMATS, so EU display never applied before.
    // English calendar labels; DD.MM.YYYY still comes from DASHBOARD_DATE_FORMATS (EU-style).
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    ...provideMomentDateAdapter(DASHBOARD_DATE_FORMATS, { useUtc: false }),
  ],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDatepickerToggle,
    MatSuffix,
    MatDateRangePicker,
    MatInput,
    NgClass,
  ],
})
export class ConversationFiltersComponent implements OnInit, OnDestroy, OnChanges {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly _countriesService = inject(CountriesService);
  private readonly _botsService = inject(BotsService);
  private readonly _projectsService = inject(ProjectsService);

  @Input() bot: TBotItem | null = null;
  @Input() visible = false;
  @Input() anchor: HTMLElement | null = null;
  @Output() filterChange = new EventEmitter<any>();

  protected readonly panel = new AnchoredPanelController({
    placement: 'above',
    gapPx: 8,
    maxHeightPx: 420,
    minHeightPx: 120,
  });

  protected languages: TLanguage[] = [];
  protected countrySearchTextInput = new FormControl('');
  protected countries: TCountry[] = [];
  protected regions: TRegion[] = [];
  private _subs: Subscription[] = [];
  protected formProjects: { name: string; _id: string }[] = [];

  public botConfigForm = this.fb.nonNullable.group({
    _id: [''],
    language: [{ name: '', code: '' }],
    countries: new FormControl<string[]>([], { nonNullable: true }),
    start: [''],
    end: [''],
  });

  ngOnInit() {
    this._botsService.fetchLanguages();
    this._subs.push(
      this._botsService.getLanguagesObservable().subscribe(res => {
        this.languages = res;
        this.updateFormForBot();
        this.cdr.markForCheck();
      })
    );
    this._subs.push(
      this._projectsService.getProjectsObservable().subscribe(results => {
        this.formProjects = results.map(project => ({
          name: project.name,
          _id: project._id as string,
        }));
        this.cdr.markForCheck();
      })
    );
    this._projectsService.setProjects();
    this._countriesService.getCountriesAndRegions();
    const { countries, regions } = this._countriesService.getCountriesAndRegionsObservable();
    this._subs.push(
      countries.subscribe(c => {
        this.countries = c;
        this.cdr.markForCheck();
      })
    );
    this._subs.push(
      regions.subscribe(r => {
        this.regions = r;
        this.cdr.markForCheck();
      })
    );

    // Emit filter changes whenever the form value changes
    this._subs.push(
      this.botConfigForm.valueChanges.subscribe(formData => {
        // You might want to add debounceTime here if changes trigger expensive operations
        // TODO: leave it for now, delete when filters will be implemented in backend, basically we wait for the response from the backend
        if (this.bot && this.bot.type && formData.language?.code !== this.bot.language) {
          this._botsService.editBot(
            {
              id: this.bot._id,
              language: formData.language?.code,
            },
            () => {
              if (this.bot) {
                this.bot.language = formData.language?.code as Language;
              }
            }
          );
        }

        // Send filter data with language as full object
        const filterData = {
          ...formData,
        };

        this.filterChange.emit(filterData);
      })
    );
  }

  ngOnDestroy() {
    this.panel.destroy();
    this._subs.forEach(sub => sub.unsubscribe());
  }

  protected getCountriesAndRegions(): (TCountry | TRegion)[] {
    return [...this.regions, ...this.countries];
  }

  protected resetCountrySearch() {
    this.countrySearchTextInput.patchValue('');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['bot'] && changes['bot'].currentValue !== changes['bot'].previousValue) {
      this.updateFormForBot();
    }

    if (changes['visible'] || changes['anchor']) {
      this.panel.update(this.visible, this.anchor);
    }
  }

  private updateFormForBot() {
    if (this.bot && this.languages.length > 0) {
      const botLanguage = this.languages.find(lang => lang.code === this.bot?.language);
      this.botConfigForm.controls.language.setValue(botLanguage || { name: '', code: '' });
    } else {
      this.botConfigForm.controls.language.setValue({ name: '', code: '' });
    }

    // Reset other form fields when bot changes
    this.botConfigForm.controls._id.setValue('');
    this.botConfigForm.controls.countries.setValue([]);
    this.botConfigForm.controls.start.setValue('');
    this.botConfigForm.controls.end.setValue('');
  }
}
